import { defineStore } from 'pinia'
import { sentry } from '@/plugins/sentry'
import type { Company, User } from '@quotetome/materials-api'

function userHasPerm(user: User | undefined, permCodename: string) {
  return user && user.qtm_permissions?.some(perm => perm.codename === permCodename)
}

interface State {
  user: User | undefined,
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: undefined,
  }),
  getters: {
    allowCustomCostCodes(state) {
      return state.user?.company?.allow_custom_cost_codes
    },

    canCreateJobsite(state) {
      return userHasPerm(state.user, 'qtm_can_create_jobsite')
    },

    canCreateChangeOrder(state) {
      return userHasPerm(state.user, 'qtm_can_create_change_order')
    },

    canCreatePO(state) {
      return userHasPerm(state.user, 'qtm_can_create_purchase_order')
    },

    canGetQuotes(state) {
      return userHasPerm(state.user, 'qtm_can_get_quotes')
    },

    canManageCompany(state) {
      return userHasPerm(state.user, 'qtm_can_manage_company')
    },

    canManageInventory(state) {
      return userHasPerm(state.user, 'qtm_can_use_inventory')
    },

    canManageInvoices(state) {
      return userHasPerm(state.user, 'qtm_can_use_invoices')
    },

    canManageUsers(state) {
      return userHasPerm(state.user, 'qtm_can_manage_users')
    },

    canUseBom(state) {
      return userHasPerm(state.user, 'qtm_can_use_bom')
    },

    canUseExpenses(state) {
      return userHasPerm(state.user, 'qtm_can_use_expenses')
    },

    canUseRequisitions(state) {
      return userHasPerm(state.user, 'qtm_can_use_requisitions')
    },

    canViewInventory(state) {
      return userHasPerm(state.user, 'qtm_can_view_inventory')
    },

    inSetupMode(state) {
      return state.user?.company?.setup_mode
    },

    isAuthenticated(state) {
      return !!state.user
    },

    isStaff(state) {
      return state.user && state.user.is_staff
    },

    purchasingEmail(state) {
      if (state.user && state.user.company && state.user.company.purchasing_email) {
        return state.user.company.purchasing_email
      }

      return ''
    },

    showJoin(state) {
      return state.user?.company?.allow_users_to_join_projects
    },

    signatureOptional(state) {
      return state.user?.company!.signature_required === 2
    },

    signatureRequired(state) {
      return state.user?.company!.signature_required === 1
    },

    userCompany(state) {
      return state.user ? state.user.company : undefined
    },
  },
  actions: {
    setUser(user: User | undefined) {
      this.user = user
      sentry.setUser(user)
    },

    updateUser(changes: User) {
      Object.assign(this.user as User, changes)
      sentry.setUser(this.user)
    },

    updateUserCompany(company: Company) {
      this.user!.company = company
    },

    setAuthToken(token: string | undefined) {
      if (!token) {
        localStorage.removeItem('authToken')
      }
    },
  }
})
